import React, { Suspense } from 'react';
import { lazyWithRetry as lazy } from 'lazy-with-retry';
import { store } from '../../ducks/store';
import { logTraceToAppInsights, logExceptionToAppInsights } from '../../services/logging/hooks';
import type { TProgressRingProps } from './h-progressring';
import type { TTextProps } from './h-text';
import type { TTabProps } from './h-tab';
import type { TTooltipProps } from './h-tooltip';
import type { TToggleProps } from './h-toggle';
import type { TRadioProps } from './h-radio';
import type { TPersonaProps } from './h-persona';
import type { TPaginationProps } from './h-pagination';
import type { TPageTitleProps } from './h-pagetitle';
import type { TOptionProps } from './h-option';
import type { TNavHeaderProps } from './h-navheader';
import type { TMessageBarProps } from './h-messagebar';
import type { TMenuItemProps } from './h-menuitem';
import type { TMenuProps } from './h-menu';
import type { TLayoutProps } from './h-layout';
import type { TIconProps } from './h-icon';
import type { THoverCardProps } from './h-hovercard';
import type { TFlyInPanelProps } from './h-flyinpanel';
import type { TFilterPillProps } from './h-filterpill';
import type { TDropdownProps } from './h-dropdown';
import type { TDividerProps } from './h-divider';
import type { TDialogProps } from './h-dialog';
import type { TDatePickerProps } from './h-datepicker';
import type { TDataGridProps } from './h-datagrid';
import type { TCommandBarProps } from './h-commandbar';
import type { TComboboxProps } from './h-combobox';
import { TRadioGroupProps } from './h-radiogroup';
import type { TAccordionProps } from './h-accordion';
import type { TAccordionItemProps } from './h-accordionitem';
import type { TBadgeProps } from './h-badge';
import type { TButtonProps } from './h-button';
import type { TCalloutProps } from './h-callout';
import type { TCardProps } from './h-card';
import type { TChartProps } from './h-chart';
import type { TSelectProps } from './h-select';
import type { TSkeletonProps } from './h-skeleton';
import type { TTabPanelProps } from './h-tabpanel';
import type { TTabsProps } from './h-tabs';
import type { TTreeProps } from './h-tree';
import type { TTreeItemProps } from './h-treeitem';
import type { TVisuallyHiddenProps } from './h-visuallyhidden';

const EnablerFetchRetryOptions = (sessionCacheKey: string) => ({
  failFallbackComponent: () => null,
  retries: 3,
  interval: 500,
  forceRefreshOptions: {
    refreshRetries: 1,
    sessionCacheKey,
    cacheKeyPrefix: 'earnings-refetch-key',
  },
  onRefresh: (error: Error) => {
    const impersonatedUser = store.getState().impersonation?.impersonatedUser;
    logTraceToAppInsights(
      'Busting cache and trying refresh for failed harmony enabler import',
      '',
      'SellerExperience',
      impersonatedUser || '',
      error
    );
  },
  onRetry: (error: Error, retriesLeft: number) => {
    const impersonatedUser = store.getState().impersonation?.impersonatedUser;
    logTraceToAppInsights(
      `Retrying fetch of harmony enabler import. Retries left: ${retriesLeft}`,
      '',
      'SellerExperience',
      impersonatedUser || '',
      error
    );
  },
  onFailure: (error: Error) => {
    const impersonatedUser = store.getState().impersonation?.impersonatedUser;
    logExceptionToAppInsights('Failed to import harmony enabler', '', 'SellerExperience', impersonatedUser || '', error);
  },
});

const AccordionImport = lazy(() => import('./h-accordion'), EnablerFetchRetryOptions('Accordion'));
export const Accordion = (props: TAccordionProps) => (
  <Suspense fallback={null}>
    <AccordionImport {...props} />
  </Suspense>
);
const AccordionItemImport = lazy(() => import('./h-accordionitem'), EnablerFetchRetryOptions('AccordionItem'));
export const AccordionItem = (props: TAccordionItemProps) => (
  <Suspense fallback={null}>
    <AccordionItemImport {...props} />
  </Suspense>
);
const BadgeImport = lazy(() => import('./h-badge'), EnablerFetchRetryOptions('Badge'));
export const Badge = (props: TBadgeProps) => (
  <Suspense fallback={null}>
    <BadgeImport {...props} />
  </Suspense>
);
const ButtonImport = lazy(() => import('./h-button'), EnablerFetchRetryOptions('Button'));
export const Button = (props: TButtonProps) => (
  <Suspense fallback={null}>
    <ButtonImport {...props} />
  </Suspense>
);
const CalloutImport = lazy(() => import('./h-callout'), EnablerFetchRetryOptions('Callout'));
export const Callout = (props: TCalloutProps) => (
  <Suspense fallback={null}>
    <CalloutImport {...props} />
  </Suspense>
);
const CardImport = lazy(() => import('./h-card'), EnablerFetchRetryOptions('Card'));
export const Card = (props: TCardProps) => (
  <Suspense fallback={null}>
    <CardImport {...props} />
  </Suspense>
);
const ChartImport = lazy(() => import('./h-chart'), EnablerFetchRetryOptions('Chart'));
export const Chart = (props: TChartProps) => (
  <Suspense fallback={null}>
    <ChartImport {...props} />
  </Suspense>
);
const ComboboxImport = lazy(() => import('./h-combobox'), EnablerFetchRetryOptions('ComboBox'));
export const Combobox = (props: TComboboxProps) => (
  <Suspense fallback={null}>
    <ComboboxImport {...props} />
  </Suspense>
);
const CommandBarImport = lazy(() => import('./h-commandbar'), EnablerFetchRetryOptions('CommandBar'));
export const CommandBar = (props: TCommandBarProps) => (
  <Suspense fallback={null}>
    <CommandBarImport {...props} />
  </Suspense>
);
const DataGridImport = lazy(() => import('./h-datagrid'), EnablerFetchRetryOptions('DataGrid'));
export const DataGrid = (props: TDataGridProps) => (
  <Suspense fallback={null}>
    <DataGridImport {...props} />
  </Suspense>
);
const DatePickerImport = lazy(() => import('./h-datepicker'), EnablerFetchRetryOptions('DatePicker'));
export const DatePicker = (props: TDatePickerProps) => (
  <Suspense fallback={null}>
    <DatePickerImport {...props} />
  </Suspense>
);
const DialogImport = lazy(() => import('./h-dialog'), EnablerFetchRetryOptions('Dialog'));
export const Dialog = (props: TDialogProps) => (
  <Suspense fallback={null}>
    <DialogImport {...props} />
  </Suspense>
);
const DividerImport = lazy(() => import('./h-divider'), EnablerFetchRetryOptions('Divider'));
export const Divider = (props: TDividerProps) => (
  <Suspense fallback={null}>
    <DividerImport {...props} />
  </Suspense>
);
const DropdownImport = lazy(() => import('./h-dropdown'), EnablerFetchRetryOptions('Dropdown'));
export const Dropdown = (props: TDropdownProps) => (
  <Suspense fallback={null}>
    <DropdownImport {...props} />
  </Suspense>
);
const FilterPillImport = lazy(() => import('./h-filterpill'), EnablerFetchRetryOptions('FilterPill'));
export const FilterPill = (props: TFilterPillProps) => (
  <Suspense fallback={null}>
    <FilterPillImport {...props} />
  </Suspense>
);
const FlyInPanelImport = lazy(() => import('./h-flyinpanel'), EnablerFetchRetryOptions('FlyInPanel'));
export const FlyInPanel = (props: TFlyInPanelProps) => (
  <Suspense fallback={null}>
    <FlyInPanelImport {...props} />
  </Suspense>
);
const HoverCardImport = lazy(() => import('./h-hovercard'), EnablerFetchRetryOptions('HoverCard'));
export const HoverCard = (props: THoverCardProps) => (
  <Suspense fallback={null}>
    <HoverCardImport {...props} />
  </Suspense>
);
const IconImport = lazy(() => import('./h-icon'), EnablerFetchRetryOptions('Icon'));
export const Icon = (props: TIconProps) => (
  <Suspense fallback={null}>
    <IconImport {...props} />
  </Suspense>
);
const LayoutImport = lazy(() => import('./h-layout'), EnablerFetchRetryOptions('Layout'));
export const Layout = (props: TLayoutProps) => (
  <Suspense fallback={null}>
    <LayoutImport {...props} />
  </Suspense>
);
const MenuImport = lazy(() => import('./h-menu'), EnablerFetchRetryOptions('Menu'));
export const Menu = (props: TMenuProps) => (
  <Suspense fallback={null}>
    <MenuImport {...props} />
  </Suspense>
);
const MenuItemImport = lazy(() => import('./h-menuitem'), EnablerFetchRetryOptions('MenuItem'));
export const MenuItem = (props: TMenuItemProps) => (
  <Suspense fallback={null}>
    <MenuItemImport {...props} />
  </Suspense>
);
const MessageBarImport = lazy(() => import('./h-messagebar'), EnablerFetchRetryOptions('MessageBar'));
export const MessageBar = (props: TMessageBarProps) => (
  <Suspense fallback={null}>
    <MessageBarImport {...props} />
  </Suspense>
);
const NavHeaderImport = lazy(() => import('./h-navheader'), EnablerFetchRetryOptions('NavHeader'));
export const NavHeader = (props: TNavHeaderProps) => (
  <Suspense fallback={null}>
    <NavHeaderImport {...props} />
  </Suspense>
);
const OptionImport = lazy(() => import('./h-option'), EnablerFetchRetryOptions('Option'));
export const Option = (props: TOptionProps) => (
  <Suspense fallback={null}>
    <OptionImport {...props} />
  </Suspense>
);
const PageTitleImport = lazy(() => import('./h-pagetitle'), EnablerFetchRetryOptions('PageTitle'));
export const PageTitle = (props: TPageTitleProps) => (
  <Suspense fallback={null}>
    <PageTitleImport {...props} />
  </Suspense>
);
const PaginationImport = lazy(() => import('./h-pagination'), EnablerFetchRetryOptions('Pagination'));
export const Pagination = (props: TPaginationProps) => (
  <Suspense fallback={null}>
    <PaginationImport {...props} />
  </Suspense>
);
const PersonaImport = lazy(() => import('./h-persona'), EnablerFetchRetryOptions('Persona'));
export const Persona = (props: TPersonaProps) => (
  <Suspense fallback={null}>
    <PersonaImport {...props} />
  </Suspense>
);
const ProgressRingImport = lazy(() => import('./h-progressring'), EnablerFetchRetryOptions('ProgressRing'));
export const ProgressRing = (props: TProgressRingProps) => (
  <Suspense fallback={null}>
    <ProgressRingImport {...props} />
  </Suspense>
);
const RadioImport = lazy(() => import('./h-radio'), EnablerFetchRetryOptions('Radio'));
export const Radio = (props: TRadioProps) => (
  <Suspense fallback={null}>
    <RadioImport {...props} />
  </Suspense>
);
const RadioGroupImport = lazy(() => import('./h-radiogroup'), EnablerFetchRetryOptions('RadioGroup'));
export const RadioGroup = (props: TRadioGroupProps) => (
  <Suspense fallback={null}>
    <RadioGroupImport {...props} />
  </Suspense>
);
const SearchBoxImport = lazy(() => import('./h-searchbox'), EnablerFetchRetryOptions('SearchBox'));
export const SearchBox = (props: any) => (
  <Suspense fallback={null}>
    <SearchBoxImport {...props} />
  </Suspense>
);
const SelectImport = lazy(() => import('./h-select'), EnablerFetchRetryOptions('Select'));
export const Select = (props: TSelectProps) => (
  <Suspense fallback={null}>
    <SelectImport {...props} />
  </Suspense>
);
const SkeletonImport = lazy(() => import('./h-skeleton'), EnablerFetchRetryOptions('Skeleton'));
export const Skeleton = (props: TSkeletonProps) => (
  <Suspense fallback={null}>
    <SkeletonImport {...props} />
  </Suspense>
);
const TabImport = lazy(() => import('./h-tab'), EnablerFetchRetryOptions('Tab'));
export const Tab = (props: TTabProps) => (
  <Suspense fallback={null}>
    <TabImport {...props} />
  </Suspense>
);
const TabPanelImport = lazy(() => import('./h-tabpanel'), EnablerFetchRetryOptions('TabPanel'));
export const TabPanel = (props: TTabPanelProps) => (
  <Suspense fallback={null}>
    <TabPanelImport {...props} />
  </Suspense>
);
const TabsImport = lazy(() => import('./h-tabs'), EnablerFetchRetryOptions('Tabs'));
export const Tabs = (props: TTabsProps) => (
  <Suspense fallback={null}>
    <TabsImport {...props} />
  </Suspense>
);
const TextImport = lazy(() => import('./h-text'), EnablerFetchRetryOptions('Text'));
export const Text = (props: TTextProps) => (
  <Suspense fallback={null}>
    <TextImport {...props} />
  </Suspense>
);
const TextAreaImport = lazy(() => import('./h-textarea'), EnablerFetchRetryOptions('TextArea'));
export const TextArea = (props: any) => (
  <Suspense fallback={null}>
    <TextAreaImport {...props} />
  </Suspense>
);
const TextFieldImport = lazy(() => import('./h-textfield'), EnablerFetchRetryOptions('TextField'));
export const TextField = (props: any) => (
  <Suspense fallback={null}>
    <TextFieldImport {...props} />
  </Suspense>
);
const ToggleImport = lazy(() => import('./h-toggle'), EnablerFetchRetryOptions('Toggle'));
export const Toggle = (props: TToggleProps) => (
  <Suspense fallback={null}>
    <ToggleImport {...props} />
  </Suspense>
);
const TooltipImport = lazy(() => import('./h-tooltip'), EnablerFetchRetryOptions('Tooltip'));
export const Tooltip = (props: TTooltipProps) => (
  <Suspense fallback={null}>
    <TooltipImport {...props} />
  </Suspense>
);
const TreeImport = lazy(() => import('./h-tree'), EnablerFetchRetryOptions('Tree'));
export const Tree = (props: TTreeProps) => (
  <Suspense fallback={null}>
    <TreeImport {...props} />
  </Suspense>
);
const TreeItemImport = lazy(() => import('./h-treeitem'), EnablerFetchRetryOptions('TreeItem'));
export const TreeItem = (props: TTreeItemProps) => (
  <Suspense fallback={null}>
    <TreeItemImport {...props} />
  </Suspense>
);
const VisuallyHiddenImport = lazy(() => import('./h-visuallyhidden'), EnablerFetchRetryOptions('VisuallyHidden'));
export const VisuallyHidden = (props: TVisuallyHiddenProps) => (
  <Suspense fallback={null}>
    <VisuallyHiddenImport {...props} />
  </Suspense>
);
